/**
 * User Schema - Validacao Zod para dados de usuario
 * @module features/chat/schemas/user.schema
 *
 * Este modulo define os schemas Zod para validacao runtime dos dados
 * de usuario coletados durante o assessment conversacional.
 *
 * Todas as mensagens de erro estao em PT-BR para melhor UX.
 */

import { z } from 'zod';
import type { UserData } from '../types';

// =============================================================================
// ERROR MESSAGES (PT-BR)
// =============================================================================

const ERROR_MESSAGES = {
  EMAIL_REQUIRED: 'Email e obrigatorio',
  EMAIL_INVALID: 'Email invalido',
  NAME_TOO_SHORT: 'Nome deve ter pelo menos 2 caracteres',
  NAME_TOO_LONG: 'Nome muito longo',
  COMPANY_REQUIRED: 'Nome da empresa e obrigatorio',
  ROLE_REQUIRED: 'Cargo e obrigatorio',
  CONSENT_REQUIRED: 'Consentimento LGPD e obrigatorio',
} as const;

// =============================================================================
// BASE SCHEMAS
// =============================================================================

/**
 * Schema para validacao isolada de email
 *
 * Valida:
 * - Formato de email valido
 * - Nao aceita string vazia
 *
 * @example
 * ```typescript
 * const result = emailSchema.safeParse('user@email.com');
 * if (result.success) {
 *   console.log(result.data); // 'user@email.com'
 * }
 * ```
 */
export const emailSchema = z
  .string()
  .min(1, { message: ERROR_MESSAGES.EMAIL_REQUIRED })
  .email({ message: ERROR_MESSAGES.EMAIL_INVALID });

/**
 * Schema para validacao de nome
 *
 * Valida:
 * - Minimo 2 caracteres
 * - Maximo 100 caracteres
 */
const nameSchema = z
  .string()
  .min(2, { message: ERROR_MESSAGES.NAME_TOO_SHORT })
  .max(100, { message: ERROR_MESSAGES.NAME_TOO_LONG });

/**
 * Schema para validacao de empresa
 *
 * Valida:
 * - Nao aceita string vazia quando fornecido
 */
const companySchema = z.string().min(1, { message: ERROR_MESSAGES.COMPANY_REQUIRED });

/**
 * Schema para validacao de cargo
 *
 * Valida:
 * - Nao aceita string vazia quando fornecido
 */
const roleSchema = z.string().min(1, { message: ERROR_MESSAGES.ROLE_REQUIRED });

/**
 * Schema para timestamp de consentimento
 *
 * Aceita:
 * - Date object
 * - ISO date string (converte para Date)
 */
const consentTimestampSchema = z.coerce.date();

// =============================================================================
// MAIN SCHEMAS
// =============================================================================

/**
 * Schema completo para UserData
 *
 * Valida todos os campos de UserData com regras especificas:
 * - name: opcional, mas se presente deve ter 2-100 caracteres
 * - email: opcional, mas se presente deve ser email valido
 * - company: opcional, mas se presente nao pode ser vazio
 * - role: opcional, mas se presente nao pode ser vazio
 * - consentGiven: boolean obrigatorio
 * - consentTimestamp: opcional, aceita Date ou ISO string
 *
 * @example
 * ```typescript
 * const result = userDataSchema.safeParse({
 *   name: 'Maria Silva',
 *   email: 'maria@empresa.com',
 *   company: 'Tech Corp',
 *   role: 'CTO',
 *   consentGiven: true,
 *   consentTimestamp: new Date(),
 * });
 * ```
 */
export const userDataSchema = z.object({
  name: nameSchema.optional(),
  email: emailSchema.optional(),
  company: companySchema.optional(),
  role: roleSchema.optional(),
  consentGiven: z.boolean(),
  consentTimestamp: consentTimestampSchema.optional(),
});

/**
 * Schema para validacao de campos obrigatorios
 *
 * Usado para validar que o usuario preencheu todos os campos
 * necessarios antes de prosseguir com o assessment.
 *
 * Campos obrigatorios:
 * - name: string (nao pode ser undefined)
 * - email: string (nao pode ser undefined)
 * - consentGiven: true (literal, nao apenas boolean)
 * - consentTimestamp: Date (nao pode ser undefined)
 *
 * Campos opcionais:
 * - company: string | undefined
 * - role: string | undefined
 *
 * @example
 * ```typescript
 * const result = requiredFieldsSchema.safeParse(userData);
 * if (result.success) {
 *   // Todos os campos obrigatorios estao preenchidos
 *   const validUser: RequiredUserFields = result.data;
 * }
 * ```
 */
export const requiredFieldsSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  company: companySchema.optional(),
  role: roleSchema.optional(),
  consentGiven: z.literal(true, {
    error: ERROR_MESSAGES.CONSENT_REQUIRED,
  }),
  consentTimestamp: consentTimestampSchema,
});

/**
 * Schema parcial para validacao incremental
 *
 * Usado para validar campos individualmente durante a coleta
 * progressiva de dados no chat conversacional.
 *
 * Todos os campos sao opcionais, mas quando fornecidos
 * devem passar nas validacoes especificas.
 *
 * @example
 * ```typescript
 * // Validar apenas o nome durante a coleta
 * const result = partialUserDataSchema.safeParse({ name: 'Maria' });
 *
 * // Validar apenas o email
 * const result2 = partialUserDataSchema.safeParse({ email: 'maria@test.com' });
 * ```
 */
export const partialUserDataSchema = z
  .object({
    name: nameSchema.optional(),
    email: emailSchema.optional(),
    company: companySchema.optional(),
    role: roleSchema.optional(),
    consentGiven: z.boolean().optional(),
    consentTimestamp: consentTimestampSchema.optional(),
  })
  .partial();

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Valida um email de forma sincrona e retorna boolean
 *
 * Funcao utilitaria para validacao imediata sem necessidade
 * de acessar o resultado do safeParse.
 *
 * @param email - String a ser validada como email
 * @returns true se o email e valido, false caso contrario
 *
 * @example
 * ```typescript
 * if (validateEmail(userInput)) {
 *   // Email valido, pode prosseguir
 * } else {
 *   // Mostrar erro ao usuario
 * }
 * ```
 */
export function validateEmail(email: string): boolean {
  return emailSchema.safeParse(email).success;
}

/**
 * Retorna lista de campos obrigatorios que estao faltando
 *
 * Verifica quais campos obrigatorios ainda nao foram preenchidos
 * pelo usuario. Util para mostrar feedback durante a coleta.
 *
 * Campos verificados:
 * - name: obrigatorio, undefined = faltando
 * - email: obrigatorio, undefined = faltando
 * - consentGiven: obrigatorio, false = faltando
 * - consentTimestamp: obrigatorio, undefined = faltando
 *
 * Campos NAO verificados (opcionais):
 * - company
 * - role
 *
 * @param userData - Dados do usuario a serem verificados
 * @returns Array com nomes dos campos faltantes
 *
 * @example
 * ```typescript
 * const missing = getMissingFields(userData);
 * if (missing.length > 0) {
 *   console.log(`Campos faltando: ${missing.join(', ')}`);
 * }
 * ```
 */
export function getMissingFields(userData: UserData): string[] {
  const missingFields: string[] = [];

  if (userData.name === undefined) {
    missingFields.push('name');
  }

  if (userData.email === undefined) {
    missingFields.push('email');
  }

  if (userData.consentGiven !== true) {
    missingFields.push('consentGiven');
  }

  if (userData.consentTimestamp === undefined) {
    missingFields.push('consentTimestamp');
  }

  return missingFields;
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

/**
 * Tipo inferido do userDataSchema
 * Equivalente a UserData do types module
 */
export type UserDataSchema = z.infer<typeof userDataSchema>;

/**
 * Tipo inferido do requiredFieldsSchema
 * Equivalente a RequiredUserFields do types module
 */
export type RequiredUserFieldsSchema = z.infer<typeof requiredFieldsSchema>;

/**
 * Tipo inferido do partialUserDataSchema
 */
export type PartialUserDataSchema = z.infer<typeof partialUserDataSchema>;
